import { CandidateDetailPanel } from "@components/campaigns/CandidateDetailPanel";
import { CandidateReferenceBadge } from "@components/candidates/CandidateReferenceBadge";
import { OpenFullProfileLink } from "@components/candidates/OpenFullProfileLink";
import { ProfileDataPanel } from "@components/candidates/ProfileDataPanel";
import { DrivePickerButton } from "@components/shared/DrivePickerButton";
import { FileDropZone } from "@components/shared/FileDropZone";
import { NotesPanel } from "@components/shared/NotesPanel";
import { RefreshIndicator } from "@components/shared/RefreshIndicator";
import {
  ATTACH_CANDIDATE_RESUME,
  DETACH_CANDIDATE_RESUME,
  UPDATE_CAMPAIGN_CANDIDATE,
} from "@lib/graphql/mutations";
import { CAMPAIGN_CANDIDATE_DETAIL_QUERY, CANDIDATE_QUERY } from "@lib/graphql/queries";
import { useRecordChannel } from "@lib/hooks/useRecordChannel";
import { ArrowLeft, ExternalLink, FileText, Pencil, Save, Trash2, Upload, X } from "lucide-react";
import { useCallback, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { useMutation, useQuery } from "urql";

const CONSENT_BADGE_STYLES: Record<string, string> = {
  not_requested: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300",
  pending: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400",
  given: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
  refused: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
  withdrawn: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
};

const CONSENT_LABELS: Record<string, string> = {
  not_requested: "Not Requested",
  pending: "Pending",
  given: "Given",
  refused: "Refused",
  withdrawn: "Withdrawn",
};

const STAGE_LABELS: Record<string, string> = {
  longlist: "Long list",
  outreach: "Outreach",
  shortlist: "Shortlist",
  assessment: "Assessment",
  client_interview: "Client interview",
  offer: "Offer",
  placement: "Placement",
  rejected: "Rejected",
  withdrawn: "Withdrawn",
};

interface CampaignCandidateHeaderData {
  id: string;
  stage: string;
  notes: string | null;
  candidate: {
    id: string;
    referenceNumber: number | null;
    fullName: string;
    email: string | null;
    linkedinUrl: string | null;
    currentRoleTitle: string | null;
    currentCompany: string | null;
    location: string | null;
  };
  campaign: {
    id: string;
    title: string;
  };
}

interface CandidateResumeData {
  id: string;
  filename: string;
  url: string;
  byteSize: number;
  createdAt: string;
}

interface CandidateData {
  id: string;
  fullName: string;
  consentStatus: string;
  consentGivenAt: string | null;
  consentNotes: string | null;
  profileData: Record<string, unknown> | null;
  resumes: CandidateResumeData[];
  attachedNotes: NoteRef[];
}

interface NoteRef {
  id: string;
  content: string;
  noteType: string;
  visibility: string;
  author: { id: string; name: string };
  createdAt: string;
}

export function CampaignCandidateDetail() {
  const { campaignId, campaignCandidateId } = useParams();
  const navigate = useNavigate();

  const [{ data: ccData, fetching: ccFetching }, refetchCc] = useQuery({
    query: CAMPAIGN_CANDIDATE_DETAIL_QUERY,
    variables: { id: campaignCandidateId },
  });
  const cc = ccData?.campaignCandidate as CampaignCandidateHeaderData | undefined;

  function handleBack() {
    if (window.history.length > 1) {
      navigate(-1);
    } else if (campaignId) {
      navigate(`/campaigns/${campaignId}/run/pipeline`);
    }
  }

  if (ccFetching && !cc) return <p className="text-sm text-text-muted">Loading...</p>;
  if (!cc) return <p className="text-sm text-text-muted">Candidate not found in this campaign</p>;

  const stageLabel = STAGE_LABELS[cc.stage] ?? cc.stage;

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <button
          type="button"
          onClick={handleBack}
          className="flex items-center gap-1 text-sm text-text-muted hover:text-text-secondary"
        >
          <ArrowLeft size={16} /> Back to {cc.campaign.title} · {stageLabel}
        </button>
        <RefreshIndicator active={ccFetching} />
      </div>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-text-primary">
            {cc.candidate.fullName}
            {typeof cc.candidate.referenceNumber === "number" && (
              <CandidateReferenceBadge
                referenceNumber={cc.candidate.referenceNumber}
                variant="detail"
              />
            )}
          </h1>
          <div className="mt-1 flex flex-wrap gap-4 text-sm text-text-muted">
            {cc.candidate.currentRoleTitle && <span>{cc.candidate.currentRoleTitle}</span>}
            {cc.candidate.currentCompany && <span>at {cc.candidate.currentCompany}</span>}
            {cc.candidate.location && <span>{cc.candidate.location}</span>}
          </div>
        </div>
        <OpenFullProfileLink candidateId={cc.candidate.id} variant="header" />
      </div>

      <CandidateExtras
        candidateId={cc.candidate.id}
        emailFromCc={cc.candidate.email}
        linkedinUrlFromCc={cc.candidate.linkedinUrl}
      />

      <CampaignAssessmentEditor
        campaignCandidateId={cc.id}
        notes={cc.notes}
        onUpdated={() => refetchCc({ requestPolicy: "network-only" })}
      />

      {/* Campaign-scoped detail (notes, outreach, interviews, assessment, offers) */}
      <div className="mt-6 rounded-lg bg-card shadow-sm ring-1 ring-ring">
        <div className="border-b border-border px-4 py-2 text-sm font-medium text-text-secondary">
          Activity in this campaign
        </div>
        <CandidateDetailPanel
          campaignCandidateId={cc.id}
          candidateId={cc.candidate.id}
          candidateName={cc.candidate.fullName}
          candidateRole={cc.candidate.currentRoleTitle ?? undefined}
          candidateCompany={cc.candidate.currentCompany ?? undefined}
        />
      </div>

      {/* Manual refresh of the campaign-scoped data */}
      <div className="mt-4 flex justify-end">
        <button
          type="button"
          onClick={() => refetchCc({ requestPolicy: "network-only" })}
          className="text-xs text-text-muted hover:text-text-secondary"
        >
          Refresh
        </button>
      </div>
    </div>
  );
}

function CandidateExtras({
  candidateId,
  emailFromCc,
  linkedinUrlFromCc,
}: {
  candidateId: string;
  emailFromCc: string | null;
  linkedinUrlFromCc: string | null;
}) {
  const [{ data, fetching }, reexecute] = useQuery({
    query: CANDIDATE_QUERY,
    variables: { id: candidateId },
  });
  const refresh = useCallback(() => reexecute({ requestPolicy: "network-only" }), [reexecute]);
  useRecordChannel("Candidate", candidateId, refresh);

  const candidate = data?.candidate as CandidateData | undefined;

  const [, attachResume] = useMutation(ATTACH_CANDIDATE_RESUME);
  const [, detachResume] = useMutation(DETACH_CANDIDATE_RESUME);
  const [uploading, setUploading] = useState(false);
  const [deletingResumeId, setDeletingResumeId] = useState<string | null>(null);

  async function handleResumeUpload(file: File) {
    if (!candidate) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content;
      const response = await fetch("/uploads", {
        method: "POST",
        headers: csrfToken ? { "X-CSRF-Token": csrfToken } : {},
        body: formData,
      });

      const uploadData = await response.json();
      await attachResumeBySignedId(uploadData.signed_id);
    } finally {
      setUploading(false);
    }
  }

  async function attachResumeBySignedId(signedId: string) {
    if (!candidate) return;
    const result = await attachResume({
      input: { candidateId: candidate.id, signedId },
    });
    if (result.data?.attachCandidateResume?.errors?.length === 0) {
      refresh();
    }
  }

  async function handleResumeDelete(attachmentId: string) {
    if (!candidate || !confirm("Delete this resume?")) return;
    setDeletingResumeId(attachmentId);
    try {
      const result = await detachResume({
        input: { candidateId: candidate.id, attachmentId },
      });
      if (result.data?.detachCandidateResume?.errors?.length === 0) {
        refresh();
      }
    } finally {
      setDeletingResumeId(null);
    }
  }

  return (
    <>
      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
        {emailFromCc && <InfoCard label="Email" value={emailFromCc} />}
        {linkedinUrlFromCc && (
          <InfoCard label="LinkedIn" value={linkedinUrlFromCc} href={linkedinUrlFromCc} />
        )}
        {candidate && (
          <div className="rounded-lg bg-card p-4 shadow-sm ring-1 ring-ring">
            <h3 className="text-sm font-medium text-text-secondary">Consent</h3>
            <div className="mt-2 flex flex-wrap items-center gap-3">
              <span
                className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                  CONSENT_BADGE_STYLES[candidate.consentStatus] ??
                  CONSENT_BADGE_STYLES.not_requested
                }`}
              >
                {CONSENT_LABELS[candidate.consentStatus] ?? candidate.consentStatus}
              </span>
              {candidate.consentGivenAt && (
                <span className="text-xs text-text-muted">
                  {new Date(candidate.consentGivenAt).toLocaleDateString()}
                </span>
              )}
            </div>
            {candidate.consentNotes && (
              <p className="mt-2 text-xs text-text-muted">{candidate.consentNotes}</p>
            )}
          </div>
        )}
      </div>

      {candidate && (
        <div className="mt-6">
          <NotesPanel
            notableType="Candidate"
            notableId={candidate.id}
            notes={candidate.attachedNotes ?? []}
            onChanged={refresh}
          />
        </div>
      )}

      <div className="mt-6">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-medium text-text-primary">Resumes</h2>
          <FileDropZone
            accept=".pdf"
            disabled={uploading}
            onFiles={(files) => handleResumeUpload(files[0])}
            overlayLabel="Drop resume here"
          >
            {({ openPicker }) => (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={openPicker}
                  disabled={uploading}
                  className="flex cursor-pointer items-center gap-2 rounded-md border border-input-border px-3 py-2 text-sm font-medium text-text-secondary hover:bg-card-hover disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Upload size={16} /> {uploading ? "Uploading..." : "Upload Resume"}
                </button>
                <DrivePickerButton
                  accept=".pdf"
                  disabled={uploading}
                  onSignedId={(signedId) => attachResumeBySignedId(signedId)}
                />
              </div>
            )}
          </FileDropZone>
        </div>
        {fetching && !candidate ? (
          <p className="mt-2 text-sm text-text-muted">Loading resumes...</p>
        ) : !candidate || candidate.resumes.length === 0 ? (
          <p className="mt-2 text-sm text-text-muted">No resumes uploaded</p>
        ) : (
          <div className="mt-2 space-y-2">
            {candidate.resumes.map((resume) => (
              <div
                key={resume.id}
                className="flex items-center gap-2 rounded-lg bg-card p-3 shadow-sm ring-1 ring-ring"
              >
                <FileText size={16} className="text-text-muted" />
                <a
                  href={resume.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-sm font-medium text-accent hover:underline"
                >
                  {resume.filename} <ExternalLink size={12} />
                </a>
                <span className="text-xs text-text-faint">
                  {Math.max(1, Math.round(resume.byteSize / 1024))} KB · uploaded{" "}
                  {new Date(resume.createdAt).toLocaleDateString()}
                </span>
                <button
                  type="button"
                  onClick={() => handleResumeDelete(resume.id)}
                  disabled={deletingResumeId === resume.id}
                  className="ml-auto rounded p-1 text-text-faint hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {candidate && <ProfileDataPanel profileData={candidate.profileData} />}
    </>
  );
}

function CampaignAssessmentEditor({
  campaignCandidateId,
  notes,
  onUpdated,
}: {
  campaignCandidateId: string;
  notes: string | null;
  onUpdated: () => void;
}) {
  const [, updateCampaignCandidate] = useMutation(UPDATE_CAMPAIGN_CANDIDATE);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(notes ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function startEdit() {
    setDraft(notes ?? "");
    setError(null);
    setEditing(true);
  }

  function cancelEdit() {
    setEditing(false);
    setError(null);
  }

  async function saveNotes() {
    setSaving(true);
    setError(null);
    const result = await updateCampaignCandidate({
      input: { id: campaignCandidateId, notes: draft },
    });
    setSaving(false);
    const errors = result.data?.updateCampaignCandidate?.errors ?? [];
    if (errors.length > 0) {
      setError(errors.join(", "));
      return;
    }
    setEditing(false);
    onUpdated();
  }

  return (
    <div className="mt-6 rounded-lg bg-card p-4 shadow-sm ring-1 ring-ring">
      <h2 className="text-sm font-medium text-text-secondary">Campaign assessment</h2>
      <div className="mt-3">
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium uppercase tracking-wide text-text-muted">
            Notes (this campaign only)
          </span>
          {!editing && (
            <button
              type="button"
              onClick={startEdit}
              data-testid="campaign-notes-edit"
              className="flex items-center gap-1 text-xs text-text-muted hover:text-text-secondary"
            >
              <Pencil size={12} /> Edit
            </button>
          )}
        </div>
        {editing ? (
          <div className="mt-2 space-y-2">
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={5}
              data-testid="campaign-notes-textarea"
              className="w-full rounded border border-input-border bg-input-bg p-2 text-sm text-text-primary"
            />
            <div className="flex gap-2">
              <button
                type="button"
                onClick={saveNotes}
                disabled={saving}
                data-testid="campaign-notes-save"
                className="flex items-center gap-1 rounded bg-accent px-3 py-1 text-xs font-medium text-white hover:bg-accent/90 disabled:opacity-50"
              >
                <Save size={12} /> {saving ? "Saving..." : "Save"}
              </button>
              <button
                type="button"
                onClick={cancelEdit}
                disabled={saving}
                className="flex items-center gap-1 rounded border border-input-border px-3 py-1 text-xs font-medium text-text-secondary hover:bg-card-hover"
              >
                <X size={12} /> Cancel
              </button>
            </div>
          </div>
        ) : (
          <p
            className="mt-2 whitespace-pre-wrap text-sm text-text-primary"
            data-testid="campaign-notes-display"
          >
            {notes && notes.trim().length > 0 ? (
              notes
            ) : (
              <span className="text-text-muted">No campaign notes yet.</span>
            )}
          </p>
        )}
        {error && <p className="mt-2 text-xs text-red-600">{error}</p>}
      </div>
    </div>
  );
}

function InfoCard({ label, value, href }: { label: string; value: string; href?: string }) {
  return (
    <div className="rounded-lg bg-card p-4 shadow-sm ring-1 ring-ring">
      <h3 className="text-sm font-medium text-text-secondary">{label}</h3>
      {href ? (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-1 flex items-center gap-1 text-sm text-accent hover:underline"
        >
          {value} <ExternalLink size={12} />
        </a>
      ) : (
        <p className="mt-1 text-sm text-text-primary">{value}</p>
      )}
    </div>
  );
}
